import { Button } from "@/components/common/Button/Button"
import { Typography } from "@/components/common/Typography/Typography"
import { UpgradeLink } from "@/components/common/UpgradeLink/UpgradeLink"
import { FeedbackSubmissionModal } from "@/components/feature/Modals/FeedbackSubmissionModal"
import { freeTrialApplies } from "@/components/hoc/FreeTrialApplies"
import useFreeTrial from "@/hooks/useFreeTrial"
import {
  useProgressFiles,
  useProgressFreeTrialsDays,
  useProgressQuestionnaires,
} from "@/hooks/useProgress"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUserLimits from "@/hooks/useUserLimits"
import { useState } from "react"
import FreeTrial, { UpgradeButton } from "./FreeTrial"

export function FreeTrialDaysLimited() {
  const value = useProgressFreeTrialsDays()
  const trial = useFreeTrial()
  const { translate } = useTranslation()
  const firstSection = translate("FREE_TRIAL/DAYS/FIRST_SECTION")
  const limitText = ` ${trial.left} ${translate("FREE_TRIAL/DAYS/DAYS")} `
  const lastSection = translate("FREE_TRIAL/DAYS/SECOND_SECTION")
  const daysUsed = trial.total - trial.left
  const usageLabel = `${daysUsed}/${trial.total}`
  // Nothing to show without a trial (still loading, or a paying user whose token
  // lists a trial group), and an unknown clock has neither a day count nor an expiry
  if (!trial.hasTrial || trial.isUnknown) return null
  return trial.isExpired ? (
    <FreeTrialExpired />
  ) : (
    <FreeTrial.Card dataTestId="free-trial-days-card">
      <FreeTrial.ProgressBar progressValue={value} usageLabel={usageLabel} />
      <FreeTrial.Text text={{ firstSection, limitText, lastSection }} />
      <FreeTrial.Buttons />
    </FreeTrial.Card>
  )
}

export function FreeTrialFileLimited({ className }: { className?: string }) {
  const value = useProgressFiles()
  const userLimits = useUserLimits()
  const filesLeft = userLimits.filesLimit - userLimits.filesUsage
  const { translate } = useTranslation()
  const firstSection = translate("FREE_TRIAL/FILES/FIRST_SECTION")
  const limitText = ` ${filesLeft} ${translate("FREE_TRIAL/FILES/FILES")} `
  const lastSection = translate("FREE_TRIAL/FILES/SECOND_SECTION")
  const usageLabel = `${userLimits.filesUsage}/${userLimits.filesLimit}`
  return (
    <FreeTrial.Card className={className}>
      <FreeTrial.ProgressBar progressValue={value} usageLabel={usageLabel} />
      <FreeTrial.Text text={{ firstSection, limitText, lastSection }} />
      <Typography variant="textXS" className="mt-2">
        <UpgradeButton />
      </Typography>
    </FreeTrial.Card>
  )
}

export function FreeTrialQuestionnairesLimited({
  className,
}: {
  className?: string
}) {
  const value = useProgressQuestionnaires()
  const userLimits = useUserLimits()
  const questionnairesLeft =
    userLimits.questionnairesLimit - userLimits.questionnairesUsage
  const { translate } = useTranslation()
  const firstSection = translate("FREE_TRIAL/QUESTIONNAIRES/FIRST_SECTION")
  const limitText = ` ${questionnairesLeft} ${translate("FREE_TRIAL/QUESTIONNAIRES/QUESTIONNAIRES")} `
  const lastSection = translate("FREE_TRIAL/QUESTIONNAIRES/SECOND_SECTION")
  const usageLabel = `${userLimits.questionnairesUsage}/${userLimits.questionnairesLimit}`
  return (
    <FreeTrial.Card className={className}>
      <FreeTrial.ProgressBar progressValue={value} usageLabel={usageLabel} />
      <FreeTrial.Text text={{ firstSection, limitText, lastSection }} />
      <Typography variant="textXS" className="mt-2">
        <UpgradeButton />
      </Typography>
    </FreeTrial.Card>
  )
}

export function FreeTrialExpired({ className }: { className?: string }) {
  const { translate } = useTranslation()
  const firstSection = ""
  const limitText = translate("FREE_TRIAL/ENDED/ENDED") + " "
  const lastSection = translate("FREE_TRIAL/ENDED/SECOND_SECTION")
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)

  return (
    <FreeTrial.Card className={className} dataTestId="free-trial-expired-card">
      <FreeTrial.ProgressBar progressValue={0} />
      <FreeTrial.Text text={{ firstSection, limitText, lastSection }} />
      <UpgradeLink>
        <Button
          variantSize="xsmall"
          variantColor="yellow"
          title={translate("FREE_TRIAL/UPGRADE")}
          className="mt-2 w-full px-12"
          label={translate("FREE_TRIAL/UPGRADE")}
        ></Button>
      </UpgradeLink>
      <Typography variant={"textXS"} className="mt-2">
        <button
          onClick={() => setIsFeedbackModalOpen(true)}
          className="text-white hover:underline"
        >
          {translate("FREE_TRIAL/FEEDBACK")}
        </button>
      </Typography>
      <FeedbackSubmissionModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
      />
    </FreeTrial.Card>
  )
}

const FreeTrialCard = {
  DaysLimited: freeTrialApplies(FreeTrialDaysLimited),
  FilesLimited: freeTrialApplies(FreeTrialFileLimited),
  QuestionnairesLimited: freeTrialApplies(FreeTrialQuestionnairesLimited),
  AccessExpired: freeTrialApplies(FreeTrialExpired),
}

export { FreeTrialCard }
